import {
	Box,
	Paper,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { type ReactNode, useState } from "react";
import {
	CONSUMPTION_CHART_METRIC,
	useConsumptionChartMetric,
} from "../../../shared/consumption/hooks/useConsumptionChartMetric";
import { ConsumptionChartHeader } from "../../../shared/consumption/components/consumption-chart/ConsumptionChartHeader";
import { ConsumptionDetailGrid } from "../../../shared/consumption/components/consumption-chart/ConsumptionDetailGrid";
import { PeriodBarsChart } from "../../../shared/consumption/components/consumption-chart/PeriodBarsChart";
import {
	GAS_CONSUMPTION_CHART_METRIC,
	type GasConsumptionChartMetric,
	type GasConsumptionMainBucket,
	type GasConsumptionSummary,
	type GasConsumptionZone,
} from "../../../shared/consumption/models/GasConsumption";
import type {
	UtilityConsumptionMode,
	UtilityConsumptionPeriod,
} from "../../../shared/consumption/models/UtilityConsumption";
import {
	GAS_RATIO_UNIT,
	GAS_UNIT,
} from "../../../shared/consumption/utils/format";
import { buildGasChartModel } from "../../../shared/consumption/utils/gasConsumption";
import {
	CONSUMPTION_CAPTION_SX,
	CONSUMPTION_LABEL_SX,
} from "../../../shared/consumption/utils/tokens";

interface GasConsumptionChartProps {
	buckets: GasConsumptionMainBucket[];
	zones: GasConsumptionZone[];
	summary: GasConsumptionSummary;
	mode: UtilityConsumptionMode;
	period: UtilityConsumptionPeriod;
	isDefaultSelection: boolean;
	controls: ReactNode;
	onZoneFilter: (zoneKey: string) => void;
}

export const GasConsumptionChart = ({
	buckets,
	zones,
	summary,
	mode,
	period,
	isDefaultSelection,
	controls,
	onZoneFilter,
}: GasConsumptionChartProps) => {
	const [highlightedBucket, setHighlightedBucket] = useState<number | null>(
		null,
	);
	const [selectedMetric, setSelectedMetric] = useConsumptionChartMetric();

	const hasMmbtuPerTon = buckets.some((bucket) => bucket.MmbtuPerTon !== null);
	const metric =
		selectedMetric === CONSUMPTION_CHART_METRIC.RATIO && hasMmbtuPerTon
			? GAS_CONSUMPTION_CHART_METRIC.MMBTU_PER_TON
			: GAS_CONSUMPTION_CHART_METRIC.MMBTU;
	const isRatio = metric === GAS_CONSUMPTION_CHART_METRIC.MMBTU_PER_TON;

	const model = buildGasChartModel({ buckets, zones, mode, metric });
	const total = isRatio ? summary.MainMmbtuPerTon : summary.MainTotalMmbtu;
	const scopeLabel = isDefaultSelection
		? "All zones"
		: model.meterIds.join(", ");

	return (
		<Paper
			component="section"
			variant="outlined"
			sx={{
				containerType: "inline-size",
				display: "flex",
				flex: 1,
				flexDirection: "column",
				minHeight: 0,
				minWidth: 0,
				p: 1.25,
			}}
		>
			<ConsumptionChartHeader
				period={period}
				isPartial={summary.IsMainPartial}
				total={total}
				unit={model.unit}
				meterScopeLabel={scopeLabel}
			/>
			<Box
				sx={{
					alignItems: "center",
					display: "flex",
					flexWrap: "wrap",
					gap: 1,
					justifyContent: "space-between",
					minWidth: 0,
					mt: 1,
					width: "100%",
				}}
			>
				{controls}
				<ToggleButtonGroup
					exclusive
					size="small"
					value={metric}
					aria-label="Gas chart metric"
					sx={{ ml: "auto" }}
					onChange={(_, value: GasConsumptionChartMetric | null) =>
						value &&
						setSelectedMetric(
							value === GAS_CONSUMPTION_CHART_METRIC.MMBTU_PER_TON
								? CONSUMPTION_CHART_METRIC.RATIO
								: CONSUMPTION_CHART_METRIC.TOTAL,
						)
					}
				>
					<ToggleButton
						value={GAS_CONSUMPTION_CHART_METRIC.MMBTU}
						aria-label="MMBtu view"
					>
						{GAS_UNIT}
					</ToggleButton>
					<Tooltip
						title={
							hasMmbtuPerTon
								? "Furnace gas per ton rolled in each period"
								: "No production tons reported for this period"
						}
					>
						<span>
							<ToggleButton
								value={GAS_CONSUMPTION_CHART_METRIC.MMBTU_PER_TON}
								aria-label="MMBtu per ton view"
								disabled={!hasMmbtuPerTon}
							>
								{GAS_RATIO_UNIT}
							</ToggleButton>
						</span>
					</Tooltip>
				</ToggleButtonGroup>
			</Box>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "minmax(0, 1fr)",
					gridTemplateRows: "auto",
					gap: 1,
					flex: "0 0 auto",
					mt: 1,
					minHeight: 0,
					minWidth: 0,
					width: "100%",
					["@container (min-width: 1140px)"]: {
						flex: 1,
						gridTemplateColumns: "minmax(0, 1fr) minmax(200px, 250px)",
						gridTemplateRows: "minmax(0, 1fr)",
					},
				}}
			>
				<Box
					sx={{
						alignSelf: "stretch",
						height: { xs: 260, sm: 300 },
						minWidth: 0,
						width: "100%",
						["@container (min-width: 1140px)"]: { height: "auto" },
					}}
				>
					<PeriodBarsChart
						model={model}
						highlightedBucket={highlightedBucket}
						onMeterFilter={onZoneFilter}
					/>
				</Box>
				<Box
					sx={{
						alignSelf: "stretch",
						display: "flex",
						flexDirection: "column",
						height: 340,
						minWidth: 0,
						width: "100%",
						["@container (min-width: 1140px)"]: { height: "auto" },
					}}
				>
					<Typography component="div" sx={{ ...CONSUMPTION_LABEL_SX, pb: 0.5 }}>
						Detail
					</Typography>
					<ConsumptionDetailGrid
						rows={model.detailRows}
						unit={model.unit}
						total={total}
						onRowHover={(rowId) =>
							setHighlightedBucket(
								rowId === null
									? null
									: (model.bucketIndexByRowId.get(rowId) ?? null),
							)
						}
					/>
				</Box>
			</Box>
		</Paper>
	);
};
